import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Button } from "@mui/material";
import GenericFormPopup from "../GenericFormPopup/GenericFormPopup";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import dayjs from "dayjs";
import { makeUpdateFieldConfigDynamic } from "../../../formConfigs/Fleet/yardRepairFieldConfig";
import { chargeFieldConfiguration } from "../../../formConfigs/Fleet/chargeFieldConfig";
import { fetchAllDrivers } from "../IncidentPopup/IncidentPopup";
import { fetchOwnerCompanies } from "../TruckPopup/TruckPopup";
import { getId } from "../../HR/UpdateEmployeePopup/UpdateEmployeePopup";

interface UpdateChargePopupProps {
  charge: any;
}

const UpdateChargePopup: React.FC<UpdateChargePopupProps> = ({ charge }) => {
  const [open, setOpen] = useState(false);
  const [initialValues, setInitialValues] = useState<any>({});
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleSnackbarClose = () => setStatus("idle");
  const queryClient = useQueryClient();
  const { token } = useAuth();

  const { data: allDrivers } = useQuery({
    queryKey: ["drivers"],
    queryFn: () => fetchAllDrivers(token),
    enabled: open && !!token,
  });

  const { data: companiesData } = useQuery({
    queryKey: ["ownerCompanies"],
    queryFn: () => fetchOwnerCompanies(token),
    enabled: open && !!token,
  });

  const chargeMutation = useMutation({
    mutationFn: (data: any) =>
      axios.put(`${apiVariable}/charges`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    onSuccess: () => {
      setStatus("success");
      handleClose();
      queryClient.invalidateQueries({ queryKey: ["charges"] });
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response?.data || "Failed to update charge");
    },
  });

  const submitHandler = (data: any) => {
    const requiredFields = ["reason", "caseNumber", "dateCommited", "amount"];
    const missingFields = requiredFields.filter((field) => !data[field]);

    if (missingFields.length > 0) {
      setStatus("error");
      setErrorMessage("Please fill in all required fields.");
      return;
    }

    if (!data.driverId && !data.ownerCompanyId) {
      setStatus("error");
      setErrorMessage("Please select a driver or a company.");
      return;
    }

    const submitData = {
      id: charge.id,
      driverId: getId(data.driverId) || null,
      ownerCompanyId: getId(data.ownerCompanyId) || null,
      reason: data.reason,
      caseNumber: data.caseNumber,
      dateCommited: dayjs(data.dateCommited).format("YYYY-MM-DD"),
      isCharged: data.isCharged,
      amount: data.amount,
      notes: data.notes || null,
    };

    chargeMutation.mutate(submitData);
  };

  useEffect(() => {
    setInitialValues({
      id: charge.id,
      driverId:
        allDrivers?.find((driver: any) => driver.id === charge.driverId) ||
        (charge.driverId
          ? {
              id: charge.driverId,
              label: charge.driverName || "Unknown Driver",
            }
          : null),
      ownerCompanyId:
        companiesData?.find(
          (company: any) => company.id === charge.ownerCompanyId
        ) ||
        (charge.ownerCompanyId
          ? {
              id: charge.ownerCompanyId,
              label: charge.ownerCompanyName || "Unknown Company",
            }
          : null),
      reason: charge.reason,
      caseNumber: charge.caseNumber,
      dateCommited: dayjs(charge.dateCommited).format("YYYY-MM-DD"),
      isCharged: charge.isCharged,
      amount: charge.amount,
      notes: charge.notes,
    });
  }, [charge, allDrivers, companiesData]);

  const dynamicFields = makeUpdateFieldConfigDynamic(chargeFieldConfiguration);

  return (
    <>
      <Button variant="contained" color="primary" onClick={handleOpen}>
        Update
      </Button>
      <GenericFormPopup
        formFields={dynamicFields}
        initialValues={initialValues}
        autocompleteData={{
          drivers: allDrivers,
          ownerCompanies: companiesData,
        }}
        title="Update Charge Form"
        onSubmit={submitHandler}
        open={open}
        onClose={handleClose}
        loading={chargeMutation.isPending}
      />
      <StatusSnackbar
        status={status}
        successMessage="Charge updated successfully"
        errorMessage={errorMessage}
        handleClose={handleSnackbarClose}
      />
    </>
  );
};

export default UpdateChargePopup;
